import React, { useRef } from 'react';
import { Box, Typography, Modal, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';

const PdfViewerModal = ({ open, onClose, pdfUrl, title }) => {
    const containerRef = useRef(null);

    // Detect mobile device
    const isMobile = typeof window !== 'undefined' && /Mobi|Android|iPhone|iPad|iPod|Opera Mini|IEMobile|WPDesktop/i.test(window.navigator.userAgent);

    React.useEffect(() => {
        if (open && pdfUrl && isMobile) {
            window.open(pdfUrl, '_blank');
            if (onClose) onClose();
        }
    }, [open, pdfUrl, isMobile, onClose]);

    if (isMobile) {
        // Don't render modal on mobile, PDF opens in new tab
        return null;
    }

    return (
        <Modal
            open={open}
            onClose={onClose}
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                m: 0,
                p: 0,
            }}
        >
            <Box
                sx={{
                    position: { xs: 'fixed', sm: 'relative' },
                    top: { xs: 0, sm: 'auto' },
                    left: { xs: 0, sm: 'auto' },
                    right: { xs: 0, sm: 'auto' },
                    bottom: { xs: 0, sm: 'auto' },
                    width: { xs: '100%', sm: '85vw', md: '75vw', lg: '65vw' },
                    height: { xs: '100%', sm: '85vh' },
                    maxHeight: { xs: '100%', sm: '85vh' },
                    bgcolor: '#525659',
                    boxShadow: 24,
                    borderRadius: { xs: 0, sm: 2 },
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                }}
            >
                {/* Header with Close Button */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        p: { xs: 1, sm: 2 },
                        gap: { xs: 1, sm: 2 },
                        background: 'linear-gradient(135deg, #1565C0 0%, #1976D2 100%)',
                        color: 'white',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                        minHeight: { xs: '56px', sm: '64px' },
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 2 }, flex: 1, minWidth: 0 }}>
                        <PictureAsPdfIcon sx={{ fontSize: { xs: 20, sm: 28 }, flexShrink: 0, display: { xs: 'none', sm: 'block' } }} />
                        <Box sx={{ minWidth: 0, overflow: 'hidden' }}>
                            <Typography variant="subtitle1" fontWeight={600} sx={{ fontSize: { xs: '0.75rem', sm: '1rem' }, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {title}
                            </Typography>
                            {/* Optionally show a static caption or leave blank */}
                            <Typography variant="caption" sx={{ opacity: 0.9, fontSize: { xs: '0.6rem', sm: '0.75rem' }, display: 'block' }}>
                                PDF Preview
                            </Typography>
                        </Box>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 }, flexShrink: 0 }}>
                        <IconButton
                            onClick={onClose}
                            size="small"
                            sx={{
                                color: 'white',
                                bgcolor: 'rgba(255,255,255,0.1)',
                                p: { xs: 0.75, sm: 1 },
                                '&:hover': {
                                    bgcolor: 'rgba(255,255,255,0.2)',
                                },
                            }}
                        >
                            <CloseIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
                        </IconButton>
                    </Box>
                </Box>

                {/* PDF Viewer using iframe for maximum compatibility */}
                <Box
                    ref={containerRef}
                    sx={{
                        flex: 1,
                        overflow: 'auto',
                        display: 'flex',
                        flexDirection: 'column',
                        bgcolor: '#525659',
                    }}
                >
                    {open && pdfUrl ? (
                        <iframe
                            src={pdfUrl}
                            title={title || 'PDF Preview'}
                            width="100%"
                            height="100%"
                            style={{ border: 'none', flex: 1, minHeight: 0, width: '100%' }}
                            allowFullScreen
                        />
                    ) : null}
                </Box>
            </Box>
        </Modal>
    );
};

export default PdfViewerModal;
